import { ArrowRight, CheckCircle2, Building2, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import DynIcon from "@/components/DynIcon";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { IMAGES, SITE } from "@/lib/site";

const USE_CASES = [
  {
    icon: Building2,
    tag: "Primary connection",
    title: "Your main business internet",
    body: "Fast, low-latency, unlimited internet built for real work — point-of-sale, cloud apps, video calls, security cameras and card processing. Local support that actually answers when you need it.",
    points: ["Unlimited data, no throttling", "Low latency for VoIP & video", "Static IP options available", "Priority local support"],
    image: IMAGES.bizTeam,
    imageAlt: "Small business team working online in a modern office",
  },
  {
    icon: ShieldCheck,
    tag: "Low-cost backup",
    title: "Failover that keeps you online",
    body: "Add an affordable secondary CYAN connection alongside your existing provider. If your main line goes down, traffic switches over automatically — so your registers keep ringing and your team keeps working.",
    points: ["Automatic failover in seconds", "Independent of your main provider", "Budget-friendly monthly rate", "Ideal for POS, phones & payments"],
    image: IMAGES.bizServer,
    imageAlt: "Network server rack providing a backup internet connection",
  },
];

const BENEFITS = [
  { icon: "Zap", title: "Built for uptime", desc: "A separate wireless path to the internet means a cut fiber line or provider outage no longer takes your business offline." },
  { icon: "DollarSign", title: "Backup without the bill shock", desc: "Redundancy used to mean paying for two full business lines. Our backup plans are priced to sit quietly in the background until you need them." },
  { icon: "RefreshCw", title: "Automatic switchover", desc: "With a dual-WAN router, failover happens on its own — no scrambling, no swapping cables, no lost sales." },
  { icon: "Headset", title: "Real local support", desc: "You talk to a neighbor in Colorado who knows your setup — not a call center reading a script." },
  { icon: "Network", title: "Right-sized for you", desc: "From a single storefront to a multi-building ranch operation, we design the connection around how you actually work." },
  { icon: "ShieldCheck", title: "Peace of mind", desc: "Payments, phones, cameras and cloud backups stay up. That's fewer emergencies and happier customers." },
];

const HOW = [
  { n: "01", t: "We assess your setup", d: "Tell us about your location, current provider and what can't afford to go down — POS, phones, cameras or all of it." },
  { n: "02", t: "We design the connection", d: "Primary or backup, we size the wireless link and recommend a dual-WAN router for automatic failover." },
  { n: "03", t: "We install & configure", d: "We mount the equipment, tune the signal to your terrain, and set failover priorities so it just works." },
  { n: "04", t: "You stay online", d: "If your main line drops, CYAN takes over in seconds. When it recovers, traffic switches back — hands-free." },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Business Internet & Failover Backup Connectivity",
  provider: { "@type": "Organization", name: "CYAN Wireless", telephone: "+1-970-440-3178" },
  areaServed: "Cortez, Dolores, Yellow Jacket & Lewis, Colorado",
  description: "Business-grade wireless internet from CYAN Wireless — as your primary connection or a low-cost automatic-failover backup that keeps your business online when your main provider goes down.",
};

export default function Business() {
  return (
    <>
      <Seo
        title="Business Internet & Backup Failover — Keep Your Business Online | CYAN Wireless"
        description="Business-grade wireless internet for Cortez, Dolores & Yellow Jacket, CO. Use CYAN as your primary connection or a low-cost automatic-failover backup that keeps your POS, phones and payments online when your main provider goes down."
        path="/business"
        jsonLd={jsonLd}
      />
      <PageHero
        overline="For business · Primary & Backup"
        titleLines={["Keep your business", <span key="2">online. <span className="text-cyan-400 text-glow">Always.</span></span>]}
        subtitle="Business-grade wireless internet built for real work — as your main connection, or as an affordable backup that automatically takes over the moment your primary line goes down. No more lost sales when the internet blinks."
        image={IMAGES.bizCables}
        imageAlt="Business network fiber cables in a data center"
      >
        <a href="#business-quote" className="inline-flex items-center gap-2 bg-cyan-500 text-ink-950 font-display font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full btn-glow hover:bg-cyan-400" data-testid="business-hero-cta">
          Get a Business Quote <ArrowRight size={16} />
        </a>
        <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 border border-white/20 text-white font-display font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full hover:border-cyan-500 hover:text-cyan-400 transition-colors" data-testid="business-hero-call">
          Call {SITE.phone}
        </a>
      </PageHero>

      {/* Two ways to use it */}
      <section className="py-24 sm:py-32 bg-ink-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="overline mb-4">Two ways to use it</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">Main line, backup line, or both.</h2>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-6">
            {USE_CASES.map((u, i) => {
              const Icon = u.icon;
              return (
                <Reveal key={u.title} delay={i * 0.1} className="group bg-ink-900 border border-white/10 rounded-3xl overflow-hidden hover:border-cyan-500/50 transition-colors">
                  <div className="h-52 overflow-hidden">
                    <img src={u.image} alt={u.imageAlt} className="w-full h-full object-cover opacity-80 group-hover:scale-105 group-hover:opacity-100 transition-all duration-500" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-11 h-11 shrink-0 grid place-items-center rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">
                        <Icon size={20} />
                      </div>
                      <span className="font-mono-tech text-[11px] uppercase tracking-widest text-cyan-400">{u.tag}</span>
                    </div>
                    <h3 className="font-display font-bold text-2xl text-white mb-3">{u.title}</h3>
                    <p className="text-zinc-400 leading-relaxed text-[15px] mb-6">{u.body}</p>
                    <ul className="space-y-2.5">
                      {u.points.map((p) => (
                        <li key={p} className="flex items-center gap-3 text-white text-sm">
                          <CheckCircle2 className="text-cyan-400 shrink-0" size={17} /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 sm:py-28 bg-ink-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="overline mb-4">Why it matters</p>
            <h2 className="font-display font-extrabold text-4xl tracking-tighter text-white leading-[0.95]">Downtime is expensive. This isn't.</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b) => (
              <motion.div key={b.title} variants={staggerItem} className="group relative bg-ink-950 border border-white/5 rounded-2xl p-7 overflow-hidden hover:border-cyan-500/40 transition-colors">
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-cyan-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <div className="w-12 h-12 grid place-items-center rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 mb-5 group-hover:scale-110 transition-transform">
                    <DynIcon name={b.icon} size={22} />
                  </div>
                  <h3 className="font-display font-bold text-xl text-white mb-2">{b.title}</h3>
                  <p className="text-zinc-400 leading-relaxed text-[15px]">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* How failover works */}
      <section className="py-24 sm:py-28 bg-ink-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-14">
            <p className="overline mb-4">How it works</p>
            <h2 className="font-display font-extrabold text-4xl tracking-tighter text-white">From outage to online in seconds.</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW.map((s) => (
              <motion.div key={s.n} variants={staggerItem} className="bg-ink-900 border border-white/5 rounded-2xl p-7 hover:border-cyan-500/40 transition-colors">
                <div className="font-mono-tech text-cyan-400 text-lg mb-4">{s.n}</div>
                <h3 className="font-display font-bold text-lg text-white mb-2">{s.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Quote form */}
      <section id="business-quote" className="py-24 sm:py-32 bg-ink-900 border-t border-white/5 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="overline mb-4">Let's talk business</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95] mb-6">
              Get a quote for your business.
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed mb-8">
              Tell us about your location and what needs to stay online. We'll recommend the right plan — primary, backup, or both — and follow up fast. Serving {SITE.areas.join(", ")} and the surrounding area.
            </p>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={IMAGES.bizServer} alt="Business backup internet equipment" className="w-full h-56 object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.1} className="bg-ink-950 border border-white/10 rounded-2xl p-8">
            <h3 className="font-display font-bold text-2xl text-white mb-2">Request a business quote</h3>
            <p className="text-zinc-400 text-sm mb-6">Tell us your business name, address and whether you want a primary or backup connection.</p>
            <LeadForm
              type="business"
              submitLabel="Request Quote"
              showAddress
              showMessage
            />
          </Reveal>
        </div>
      </section>
    </>
  );
}
